import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import { v4 as uuidv4 } from "uuid";
import { ADMIN_LANG, AVAILABLE_LANGUAGES } from "../../../../settings";
import { changeQuestionModal } from "../../../../app/slices/modals/questionEditModal";
import { PlusCircle, Trash } from "react-bootstrap-icons";
import { Col, Form, Row } from "react-bootstrap";

type Props = {
    language: string;
    handlePointsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    is_draft: boolean;
};

const QuestionEditConnect = ({ language, handlePointsChange, is_draft }: Props) => {
    const { connect_options, points } = useAppSelector((state) => state.questionEditModal);
    const dispatch = useAppDispatch();

    const handleAddOption = () => {
        if (!is_draft) return;
        const newOption = {
            option_id: uuidv4(),
            key: uuidv4(),
            points: 0,
            languages: AVAILABLE_LANGUAGES.map(lang => ({
                language_code: lang,
                option1: '',
                option2: ''
            }))
        };
        dispatch(changeQuestionModal({ connect_options: [...connect_options, newOption] }));
    };

    const handleRemoveOption = (id: string) => {
        if (!is_draft) return;
        dispatch(changeQuestionModal({ connect_options: connect_options.filter(option => option.option_id !== id) }));
    };

    const handleOption1Change = (id: string, lang: string, option1: string) => {
        const updatedOptions = connect_options.map(option => {
            if (option.option_id === id) {
                return {
                    ...option,
                    languages: option.languages.map(languageOption =>
                        languageOption.language_code === lang
                            ? { ...languageOption, option1 }
                            : languageOption
                    )
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({ connect_options: updatedOptions }));
    };

    const handleOption2Change = (id: string, lang: string, option2: string) => {
        const updatedOptions = connect_options.map(option => {
            if (option.option_id === id) {
                return {
                    ...option,
                    languages: option.languages.map(languageOption =>
                        languageOption.language_code === lang
                            ? { ...languageOption, option2 }
                            : languageOption
                    )
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({ connect_options: updatedOptions }));
    };

    return (
        <Col xs={12}>
            <Form.Group className='pb-3' controlId={`points_${language}`}>
                <Form.Label>{ADMIN_LANG.points}</Form.Label>
                <Form.Control
                    type='number'
                    onChange={handlePointsChange}
                    value={points}
                    disabled={!is_draft}
                />
            </Form.Group>
            <h4 className='pb-2 pt-3'>
                {ADMIN_LANG.connect_options}{' '}
                <PlusCircle
                    onClick={handleAddOption}
                    className={`utility-icon ${is_draft ? "green" : "disabled"}`}
                />
            </h4>
            <Row>
                <Col xs={12}>
                    <Form.Label>
                        {`${ADMIN_LANG.correct_pairs} (${language.toUpperCase()})`}
                    </Form.Label>
                </Col>
            </Row>
            <Row className='gy-3'>
                {connect_options.map((option, index) => {
                    const languageOption = option.languages.find(lang => lang.language_code === language);

                    // Generate the help text
                    const optionHelpText = option.languages
                        .filter(lang => lang.language_code !== language && (lang.option1 || lang.option2))
                        .map(lang => (
                            <div key={lang.language_code}>
                                <strong>{lang.language_code.toUpperCase()}: </strong>
                                {lang.option1} - {lang.option2}
                            </div>
                        ));

                    return (
                        <Col xs={12} key={option.key}>
                            <Row>
                                <Col xs={5}>
                                    <Form.Group controlId={`option_${index}_${language}`}>
                                        <Form.Control
                                            type='text'
                                            className={`form-control ${!is_draft ? 'disabled' : ''}`}
                                            value={languageOption?.option1 || ''}
                                            onChange={(e) => handleOption1Change(option.option_id, language, e.target.value)}
                                        />
                                        {optionHelpText.length > 0 && (
                                            <Form.Text className='help-text'>
                                                {optionHelpText}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col xs={5}>
                                    <Form.Group controlId={`option2_${index}_${language}`}>
                                        <Form.Control
                                            type='text'
                                            className={`form-control ${!is_draft ? 'disabled' : ''}`}
                                            value={languageOption?.option2 || ''}
                                            onChange={(e) => handleOption2Change(option.option_id, language, e.target.value)}
                                        />
                                        {optionHelpText.length > 0 && (
                                            <Form.Text>
                                                {optionHelpText}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col xs={2} className='d-flex justify-content-center align-items-end'>
                                    <Trash
                                        onClick={() => handleRemoveOption(option.option_id)}
                                        className={`utility-icon ${!is_draft ? 'disabled' : 'red'}`}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    );
                })}
            </Row>
        </Col>
    );
};

export default QuestionEditConnect;